import React, { useState } from 'react';
import { transcriptsService } from '../../services/transcripts';
import { Transcript } from '../../types';
import { CONTAINER_STYLES } from '../../styles/containers';
import EditButtons from '../ui/EditButtons';

interface TranscriptNotesProps {
  transcript: Transcript;
  audioFileId: string;
  onTranscriptUpdate: (updatedTranscript: Transcript) => void;
}

const TranscriptNotes: React.FC<TranscriptNotesProps> = ({
  transcript,
  audioFileId,
  onTranscriptUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleEditStart = () => {
    setEditedNotes(transcript.notes || '');
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
  };

  const handleEditSave = async () => {
    setIsLoading(true);
    try {
      const transcript = await transcriptsService.updateTranscript(
        audioFileId,
        {
          notes: editedNotes.trim(),
        }
      );
      onTranscriptUpdate(transcript);
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update transcript notes:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleEditSave();
    } else if (e.key === 'Escape') {
      handleEditCancel();
    }
  };

  return isEditing ? (
    <div className={CONTAINER_STYLES.editMode}>
      <div className="flex items-center gap-2 mb-3">
        <span className="font-medium text-gray-900 dark:text-gray-100">
          Notes
        </span>
      </div>
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Notes
          </label>
          <textarea
            value={editedNotes}
            onChange={(e) => setEditedNotes(e.target.value)}
            onKeyDown={handleKeyDown}
            className={`${CONTAINER_STYLES.input} resize-none`}
            placeholder="Add notes about this transcript..."
            rows={3}
            autoFocus
          />
        </div>
        <EditButtons
          onSave={handleEditSave}
          onCancel={handleEditCancel}
          isLoading={isLoading}
        />
      </div>
    </div>
  ) : (
    <div
      className={`${CONTAINER_STYLES.showMode} group cursor-pointer`}
      onClick={handleEditStart}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="font-medium text-gray-900 dark:text-gray-100">
          Notes
        </span>
      </div>
      <div className="space-y-2">
        {transcript.notes ? (
          <div className="text-sm">
            <p className="text-gray-900 dark:text-gray-100 whitespace-pre-wrap">
              {transcript.notes}
            </p>
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">
            No notes added yet. Click to add notes.
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptNotes;
